// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Chip } from '@mui/material';
import { AppIcon } from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  useDocumentsCardList,
  type UseDocumentsCardListProps,
} from './use-documents-card-list';

export type DocumentsCardListProps = UseDocumentsCardListProps;

export const DocumentsCardList = defineComponent({
  displayName: 'DocumentsCardList',
  useSetup: useDocumentsCardList,
  View: ({
    data,
    scopeChipColor,
    getClassificationLine,
    getSubtitleLine,
    getScopeLabel,
    getDateLabels,
    getFileCountLabel,
    onViewDetail,
    t,
  }) => (
    <div className="flex flex-col gap-3" data-testid="documents-card-list">
      {data.map(document => {
        const subtitle = getSubtitleLine(document);
        const classification = getClassificationLine(document);
        const dateLabels = getDateLabels(document);
        const fileCountLabel = getFileCountLabel(document);

        return (
          <article
            key={document.id}
            role="button"
            tabIndex={0}
            onClick={() => onViewDetail(document)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onViewDetail(document);
              }
            }}
            className="cursor-pointer rounded-lg bg-section p-4 outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            data-testid={`documents-card-${document.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-page">
                <AppIcon icon="FileText" color="inherit" size="lg" />
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="truncate font-medium leading-snug text-title-dark">
                  {document.title}
                </h3>

                {subtitle ? (
                  <p className="mt-1 text-sm text-primary-medium">{subtitle}</p>
                ) : null}

                {classification ? (
                  <p className="mt-1 text-sm text-primary-medium">
                    {classification}
                  </p>
                ) : null}

                <div className="mt-2 flex flex-wrap gap-2">
                  <Chip
                    label={getScopeLabel(document)}
                    size="small"
                    variant="outlined"
                    color={scopeChipColor[document.scope]}
                    className="!max-h-6 !text-xs"
                  />
                  {fileCountLabel ? (
                    <Chip
                      label={fileCountLabel}
                      size="small"
                      variant="outlined"
                      className="!max-h-6 !text-xs"
                    />
                  ) : null}
                </div>
              </div>
            </div>

            {dateLabels.length > 0 ? (
              <footer className="mt-3 flex justify-end border-t border-border-divider-light pt-3 text-sm text-primary-medium">
                <div className="flex shrink-0 flex-col items-end gap-0.5 text-right text-title-dark">
                  {dateLabels.map((label, index) => (
                    <span key={`${document.id}-date-${index}`}>{label}</span>
                  ))}
                </div>
              </footer>
            ) : null}
          </article>
        );
      })}
    </div>
  ),
});
